import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  ListItemText,
  MenuItem,
  Rating,
  Typography
} from '@mui/material';
import React, { useState } from 'react';
import { TrackedSelect, TrackedTextField } from 'src/theme/StyledFields';
import { ProgressSurveyContext, ProgressSurveySubmission, SurveyQuestion } from '../../types/survey';

interface SurveyFormProps {
  context: ProgressSurveyContext;
  lessonPlanId: number;
  respondentType: 'parent' | 'coach' | 'athlete' | 'payer';
  onSubmit: (data: ProgressSurveySubmission) => Promise<void>;
  loading?: boolean;
  error?: string | null;
}

// Frontend-only sentinel: explicit "no areas apply" (normalized to [] for API).
const NONE_DOMAIN_VALUE = '__none__';
const NONE_DOMAIN_CHOICE = {
  value: NONE_DOMAIN_VALUE,
  label: 'None',
  description: 'No areas apply.',
};

const SurveyForm: React.FC<SurveyFormProps> = ({
  context,
  lessonPlanId,
  respondentType,
  onSubmit,
  loading = false,
  error = null,
}) => {
  const isFinal = context.survey_type === 'final';
  const [formData, setFormData] = useState({
    excels_in_domains: [] as string[],
    growth_opportunities_domains: [] as string[],
    progress_changes: '',
    feedback_suggestions: '',
    satisfaction_rating: undefined as number | undefined,
    lasting_changes: isFinal ? '' : undefined as string | undefined,
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const questions: SurveyQuestion[] = context.survey_questions || [];

  const handleDomainChange = (field: 'excels_in_domains' | 'growth_opportunities_domains', value: string[]) => {
    let nextValue = value;
    const hadNoneSelected = formData[field].includes(NONE_DOMAIN_VALUE);
    const hasNoneSelected = value.includes(NONE_DOMAIN_VALUE);
    // Selecting None clears any real domains; selecting a real domain clears None.
    nextValue = hasNoneSelected && !hadNoneSelected
      ? [NONE_DOMAIN_VALUE]
      : value.filter(v => v !== NONE_DOMAIN_VALUE);
    if (nextValue.length > 3) return;
    setFormData(prev => ({ ...prev, [field]: nextValue }));
    if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  const handleTextChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  const handleRatingChange = (value: number | null) => {
    setFormData(prev => ({ ...prev, satisfaction_rating: value || undefined }));
    if (errors.satisfaction_rating) setErrors(prev => { const n = { ...prev }; delete n.satisfaction_rating; return n; });
  };

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};
    if (formData.excels_in_domains.length === 0) {
      newErrors.excels_in_domains = 'Please select 1–3 domains, or select None';
    } else if (formData.excels_in_domains.length > 3) {
      newErrors.excels_in_domains = 'Please select up to 3 domains';
    }
    if (formData.growth_opportunities_domains.length === 0) {
      newErrors.growth_opportunities_domains = 'Please select 1–3 domains, or select None';
    } else if (formData.growth_opportunities_domains.length > 3) {
      newErrors.growth_opportunities_domains = 'Please select up to 3 domains';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const payload: ProgressSurveySubmission = {
      lesson_plan: lessonPlanId,
      session_number: context.session_number,
      respondent_type: respondentType,
      excels_in_domains: formData.excels_in_domains.includes(NONE_DOMAIN_VALUE)
        ? []
        : formData.excels_in_domains,
      growth_opportunities_domains: formData.growth_opportunities_domains.includes(NONE_DOMAIN_VALUE)
        ? []
        : formData.growth_opportunities_domains,
      progress_changes: formData.progress_changes,
      feedback_suggestions: formData.feedback_suggestions,
    };
    if (isFinal) {
      payload.satisfaction_rating = formData.satisfaction_rating;
      payload.lasting_changes = formData.lasting_changes;
    }
    await onSubmit(payload);
  };

  const getDomainChoices = () => {
    const q = questions.find(x => x.id === 'excels_in_domains');
    return q?.choices || [];
  };
  const domainChoices = getDomainChoices();
  const domainChoicesWithNone = [NONE_DOMAIN_CHOICE, ...domainChoices];

  const isAthlete = respondentType === 'athlete';
  const participant = context.role_labels?.athlete ?? 'athlete';

  const introText = isAthlete
    ? `Please complete this survey about your progress.`
    : `Please complete this survey about ${context.athlete_name}'s progress.`;

  const excelsLabel = isAthlete
    ? 'Which area(s) do you feel you currently excel most in? Select 1–3, or select None if no areas apply. *'
    : `Which area(s) do you feel your ${participant} currently excels most in? Select 1–3, or select None if no areas apply. *`;

  const growthLabel = isAthlete
    ? 'Which area(s) do you feel you have the most opportunities to grow in? Select 1–3, or select None if no areas apply. *'
    : `Which area(s) do you feel your ${participant} has the most opportunities to grow in? Select 1–3, or select None if no areas apply. *`;

  const progressLabel = isAthlete
    ? 'Thinking about the past few weeks, what changes or progress have you noticed in yourself? (optional)'
    : `Thinking about the past few weeks, what changes or progress have you noticed in your ${participant}? (optional)`;

  const lastingChangesLabel = isAthlete
    ? 'Looking back over the entire program, what lasting changes have you noticed in your mindset, confidence, or approach to your sport? (optional)'
    : `Looking back over the entire program, what lasting changes have you noticed in your ${participant}'s mindset, confidence, or approach to their sport? (optional)`;

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 800, mx: 'auto', p: 3 }} data-attr="survey-form_submit">
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      <Typography variant="h5" gutterBottom>Survey - Session {context.session_number}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        {introText}
      </Typography>

      <FormControl fullWidth error={!!errors.excels_in_domains} sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>{excelsLabel}</Typography>
        <TrackedSelect
          multiple
          value={formData.excels_in_domains}
          onChange={(e) => handleDomainChange('excels_in_domains', e.target.value as string[])}
          data-attr="survey-form_field-excels-in-domains"
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((v) => {
                const c = domainChoicesWithNone.find(x => x.value === v);
                return <Chip key={v} label={c?.label || v} size="small" />;
              })}
            </Box>
          )}
        >
          {domainChoicesWithNone.map((c) => (
            <MenuItem key={c.value} value={c.value}>
              <Checkbox checked={formData.excels_in_domains.indexOf(c.value) > -1} />
              <ListItemText primary={c.label} secondary={c.description} />
            </MenuItem>
          ))}
        </TrackedSelect>
        <FormHelperText>
          {errors.excels_in_domains || (formData.excels_in_domains.includes(NONE_DOMAIN_VALUE)
            ? 'Selected: None'
            : `Selected: ${formData.excels_in_domains.length}/3`)}
        </FormHelperText>
      </FormControl>

      <FormControl fullWidth error={!!errors.growth_opportunities_domains} sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>{growthLabel}</Typography>
        <TrackedSelect
          multiple
          value={formData.growth_opportunities_domains}
          onChange={(e) => handleDomainChange('growth_opportunities_domains', e.target.value as string[])}
          data-attr="survey-form_field-growth-opportunities-domains"
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((v) => {
                const c = domainChoicesWithNone.find(x => x.value === v);
                return <Chip key={v} label={c?.label || v} size="small" />;
              })}
            </Box>
          )}
        >
          {domainChoicesWithNone.map((c) => (
            <MenuItem key={c.value} value={c.value}>
              <Checkbox checked={formData.growth_opportunities_domains.indexOf(c.value) > -1} />
              <ListItemText primary={c.label} secondary={c.description} />
            </MenuItem>
          ))}
        </TrackedSelect>
        <FormHelperText>
          {errors.growth_opportunities_domains || (formData.growth_opportunities_domains.includes(NONE_DOMAIN_VALUE)
            ? 'Selected: None'
            : `Selected: ${formData.growth_opportunities_domains.length}/3`)}
        </FormHelperText>
      </FormControl>

      <TrackedTextField fullWidth multiline rows={4}
        label={"Progress"}
        helperText={errors.progress_changes ? errors.progress_changes : progressLabel}
        value={formData.progress_changes}
        onChange={(e) => handleTextChange('progress_changes', e.target.value)}
        error={!!errors.progress_changes}
        sx={{ mb: 3 }} data-attr="survey-form_field-progress-changes" />
      <TrackedTextField fullWidth multiline rows={4}
        label={"Suggestions"}
        helperText={errors.feedback_suggestions ? errors.feedback_suggestions : 'You may also share any feedback, highlights, or suggestions that could make the experience stronger (optional)'}
        value={formData.feedback_suggestions}
        onChange={(e) => handleTextChange('feedback_suggestions', e.target.value)}
        error={!!errors.feedback_suggestions}
        sx={{ mb: 3 }} data-attr="survey-form_field-feedback-suggestions" />

      {isFinal && (
        <>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>How would you rate your overall satisfaction with the program? (optional)</Typography>
            <Rating value={formData.satisfaction_rating || 0} onChange={(_, v) => handleRatingChange(v)} max={5} size="large" data-attr="survey-form_field-satisfaction-rating" />
            {errors.satisfaction_rating && <FormHelperText error sx={{ mt: 1 }}>{errors.satisfaction_rating}</FormHelperText>}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{formData.satisfaction_rating ? `${formData.satisfaction_rating} out of 5` : 'Optional — select a rating if you would like'}</Typography>
          </Box>
          <TrackedTextField fullWidth multiline rows={4} label={lastingChangesLabel} value={formData.lasting_changes || ''} onChange={(e) => handleTextChange('lasting_changes', e.target.value)} error={!!errors.lasting_changes} helperText={errors.lasting_changes} sx={{ mb: 3 }} data-attr="survey-form_field-lasting-changes" />
        </>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
        <Button type="submit" variant="contained" disabled={loading} size="large" data-attr="survey-form_submit-button">{loading ? <CircularProgress size={24} /> : 'Submit Survey'}</Button>
      </Box>
    </Box>
  );
};

export default SurveyForm;
